export const en = {
  // Meta
  siteTitle: 'Intervio',
  siteDescription: 'AI-powered interview tool with real-time hints',

  // Navigation
  navHome: 'Home',
  navFeatures: 'Features',
  navHowItWorks: 'How It Works',
  navPrivacy: 'Privacy',
  navDownload: 'Download',
  joinWaitlist: 'Download',
  navLogin: 'Log In',
  navDashboard: 'Dashboard',

  // Hero Section
  heroBadge: 'AI-Powered Interview Tool',
  heroTitle1: 'Ace Your Next',
  heroTitle2: 'Interview',
  heroTitle3: 'With Real-Time AI Hints',
  heroDescription: 'Get invisible, real-time AI hints during live interviews. Intervio listens to your interview, detects questions, reads coding problems from your screen, and gives you Answer, Depth, and Lateral hints — all hidden from Zoom, Meet & Teams screen sharing.',
  heroCtaPrimary: 'Download Now',
  heroCtaSecondary: 'See How It Works',
  heroStat1: 'Invisible to Screen Share',
  heroStat2: 'Sub-second AI Hints',
  heroStat3: 'macOS · Windows · Linux · Android',

  // Hero Mockup
  mockupTitle: 'Intervio',
  mockupTranscript: 'Transcript',
  mockupInterviewer: 'Interviewer:',
  mockupQuestion: '"Can you explain how you would design a rate limiter?"',
  mockupDetectedQuestion: 'Detected Question',
  mockupQuestionText: 'Design a rate limiter system',
  mockupQuestionType: 'Technical - System Design',
  mockupAiHints: 'AI Hints',
  mockupAnswer: 'Answer',
  mockupAnswerHint: 'Use a token bucket algorithm with configurable rate and burst size. Store counters in Redis for distributed limiting...',
  mockupGoDeeper: 'Go Deeper',
  mockupDepthHint: 'Mention token bucket vs sliding window algorithms, discuss trade-offs...',
  mockupRelated: 'Related',
  mockupLateralHint: 'Connect to distributed systems experience, Redis usage...',

  // Features Section
  featuresTitle: 'Everything You Need for Interview Success',
  featuresSubtitle: 'Powerful features designed to help you perform effectively during interviews.',

  feature1Title: 'Real-Time Transcription',
  feature1Desc: 'Automatic speech-to-text that captures every word of your interview conversation as it happens.',

  feature2Title: 'Coding Interview Copilot',
  feature2Desc: 'Reads coding problems directly from your screen on HackerRank, CodeSignal, LeetCode, and CoderPad. Get instant hints for algorithms, data structures, and system design.',

  feature3Title: 'AI Question Detection',
  feature3Desc: 'Intelligent detection of questions being asked, understanding both technical and behavioral formats.',
  
  feature4Title: 'Instant Answer Hints',
  feature4Desc: 'Get real-time suggestions to help you structure answers and remember key points during practice.',
  
  feature5Title: 'Depth Hints',
  feature5Desc: 'Suggestions to help you go deeper on topics, showing technical expertise and thorough understanding.',
  
  feature6Title: 'Lateral Hints',
  feature6Desc: 'Ideas to connect your answer to related experiences and demonstrate breadth of knowledge.',
  
  feature7Title: 'Invisible & Private',
  feature7Desc: 'Invisible to Zoom, Teams, Google Meet screen sharing. Your interviewer will never see Intervio. Choose local processing for zero data leaving your device.',

  feature8Title: 'Cross-Platform Support',
  feature8Desc: 'Works seamlessly on macOS, Windows, Linux, and Android. Practice anywhere, on any device.',

  // How It Works Section
  howItWorksTitle: 'How Intervio Works',
  howItWorksSubtitle: 'A seamless experience designed to help you practice without interruption.',
  
  step1Title: 'Start Your Interview Session',
  step1Desc: 'Launch the desktop app and begin your session. The app starts capturing audio and screen content via OCR.',

  step2Title: 'Speak Naturally',
  step2Desc: 'Conduct your interview naturally. The speech-to-text engine transcribes everything in real-time, while OCR reads coding problems from your screen.',

  step3Title: 'Questions Are Detected',
  step3Desc: "AI identifies when questions are being asked and determines if they're technical, behavioral, or follow-up questions.",
  
  step4Title: 'Get Instant Hints',
  step4Desc: 'Receive real-time hints to guide your answers: direct answer suggestions, depth hints for technical detail, and lateral hints for related experiences.',

  architectureTitle: 'How It Works Under the Hood',
  architectureDesc: 'Audio and screen content are captured and processed using either cloud AI services or local models for transcription and OCR. You can choose cloud mode for maximum accuracy or local mode for fully offline operation. Text transcripts are then sent to AI for question detection and hint generation.',

  startSession: 'Start Session',
  transcribing: 'Transcribing...',
  questionDetected: 'Question Detected',
  technical: 'Technical',
  answerHint: 'Answer',
  depthHint: 'Depth Hint',
  lateralHint: 'Lateral Hint',

  // Privacy Section (Homepage)
  privacyTitle: 'How We Handle Your Data',
  privacySubtitle: "Intervio supports both cloud and local processing modes. Choose cloud services for maximum accuracy or local models for fully offline, private operation.",

  privacy1Title: 'Audio Processing',
  privacy1Desc: 'Audio is captured and transcribed using cloud speech-to-text services or local Whisper models running entirely on your device.',

  privacy2Title: 'Screen Capture & OCR',
  privacy2Desc: 'Screen content is captured and processed via cloud OCR or local OCR engine to read coding problems, diagrams, and visual context.',

  privacy3Title: 'AI Hint Generation',
  privacy3Desc: 'Text transcripts and OCR results are sent to AI for question detection and intelligent hint generation.',

  privacy4Title: 'No Data Storage',
  privacy4Desc: "We don't store your interview sessions long-term. Data is processed in real-time for immediate assistance.",

  dataFlowTitle: 'How Your Data Flows',
  yourComputer: 'Your Computer',
  audioCapture: 'Audio Capture',
  screenCapture: 'Screen Capture',
  sttProcessing: 'STT Processing',
  ocrProcessing: 'OCR Processing',
  textOnly: 'Text Only',
  cloudAi: 'Cloud AI',
  questionDetection: 'Question Detection',
  hintGeneration: 'Hint Generation',
  hints: 'Hints',
  yourScreen: 'Your Screen',
  displayHints: 'Display Hints',
  showTranscript: 'Show Transcript',

  // Download CTA Section
  downloadCtaBadge: 'Available Now',
  downloadCtaTitle: 'Ready to Ace Your Interview?',
  downloadCtaSubtitle: 'Download Intervio and get real-time AI hints during your next interview. Available for macOS, Windows, Linux, and Android.',
  downloadCtaButton: 'Download Intervio',
  downloadCtaFeature1: 'Free 3-hour trial included',
  downloadCtaFeature2: 'All features unlocked',
  downloadCtaFeature3: 'No credit card required',

  // Footer
  footerTagline: 'Get real-time AI hints during your interview sessions.',
  footerProduct: 'Product',
  footerResources: 'Resources',
  footerLegal: 'Legal',
  footerContact: 'Contact',
  footerTerms: 'Terms of Service',
  footerRights: 'All rights reserved.',
  footerDisclaimer: 'This tool is designed to assist during interview sessions.',

  // Features Page
  featuresPageTitle: 'Powerful Features for Interview Success',
  featuresPageSubtitle: 'Every feature is designed to help you perform effectively during interviews. Choose between cloud-powered or local processing for real-time assistance.',

  categoryAudioSpeech: 'Audio & Speech',
  categoryVisualIntelligence: 'Visual Intelligence',
  categoryAiHints: 'AI-Powered Hints',
  
  featureRealTimeSpeech: 'Real-Time Speech Recognition',
  featureRealTimeSpeechDesc: 'Our advanced speech-to-text engine processes your voice in real-time, delivering accurate transcriptions as you speak during interviews.',
  featureRealTimeSpeechDetail1: 'Supports multiple languages and accents',
  featureRealTimeSpeechDetail2: 'Handles technical jargon and terminology',
  featureRealTimeSpeechDetail3: 'Cloud or local processing — your choice',
  featureRealTimeSpeechDetail4: 'Low latency for natural conversation flow',
  
  featureSpeakerDiff: 'Speaker Differentiation',
  featureSpeakerDiffDesc: 'Intelligently distinguishes between different speakers to properly attribute questions from the interviewer and responses from you.',
  featureSpeakerDiffDetail1: 'Automatic role detection',
  featureSpeakerDiffDetail2: 'Context-aware attribution',
  featureSpeakerDiffDetail3: 'Works with video calls and in-person practice',
  
  featureScreenRecognition: 'Screen Content Recognition',
  featureScreenRecognitionDesc: 'OCR technology reads text from your screen, capturing coding problems, system design diagrams, and technical documentation. Works with cloud or local OCR engines.',
  featureScreenRecognitionDetail1: 'Code syntax recognition',
  featureScreenRecognitionDetail2: 'Diagram and chart understanding',
  featureScreenRecognitionDetail3: 'Multi-monitor support',
  featureScreenRecognitionDetail4: 'Selective region capture',
  
  featureContextBuilding: 'Context Building',
  featureContextBuildingDesc: 'Combines audio transcription with screen content to build a comprehensive understanding of the interview context.',
  featureContextBuildingDetail1: 'Multimodal timeline construction',
  featureContextBuildingDetail2: 'Relevant context prioritization',
  featureContextBuildingDetail3: 'Historical session reference',
  
  featureAnswerHints: 'Answer Hints',
  featureAnswerHintsDesc: 'Direct answer suggestions that help you structure your response with key points, frameworks, and talking points.',
  featureAnswerHintsDetail1: 'Structured answer frameworks',
  featureAnswerHintsDetail2: 'Key points and talking points',
  featureAnswerHintsDetail3: 'Algorithm and approach suggestions',
  featureAnswerHintsDetail4: 'Step-by-step solution outlines',

  featureDepthHints: 'Depth Hints',
  featureDepthHintsDesc: 'Suggestions that help you dive deeper into a topic, demonstrating thorough technical understanding and expertise.',
  featureDepthHintsDetail1: 'Technical elaboration points',
  featureDepthHintsDetail2: 'Edge case considerations',
  featureDepthHintsDetail3: 'Performance optimization angles',
  featureDepthHintsDetail4: 'Best practice reminders',
  
  featureLateralHints: 'Lateral Hints',
  featureLateralHintsDesc: 'Ideas to connect your answer to related experiences, projects, and skills, showing breadth of knowledge.',
  featureLateralHintsDetail1: 'Related technology suggestions',
  featureLateralHintsDetail2: 'Project connection points',
  featureLateralHintsDetail3: 'Experience bridging ideas',
  featureLateralHintsDetail4: 'Transferable skill highlights',
  
  featureQuestionClass: 'Question Classification',
  featureQuestionClassDesc: 'AI automatically classifies questions by type to provide the most relevant guidance and hint format.',
  featureQuestionClassDetail1: 'Technical questions (coding, system design)',
  featureQuestionClassDetail2: 'Behavioral questions (STAR format)',
  featureQuestionClassDetail3: 'Follow-up and clarification questions',
  featureQuestionClassDetail4: 'Conceptual and theoretical questions',

  // How It Works Page
  howItWorksPageTitle: 'How Intervio Works',
  howItWorksPageSubtitle: 'A seamless approach to interview assistance. Understand the technology behind real-time AI assistance.',

  techStackTitle: 'Technology Behind the Magic',
  techStackSubtitle: 'Built with modern technologies chosen for performance, privacy, and reliability.',
  
  techDesktopTitle: 'Desktop Application',
  techDesktopDesc: 'Cross-platform app built with Tauri, combining the performance of Rust with the flexibility of React.',
  
  techSpeechTitle: 'Speech Recognition',
  techSpeechDesc: 'STT powered by Whisper models — run in the cloud for maximum accuracy or locally on your device for offline use.',

  techScreenTitle: 'Screen Analysis & OCR',
  techScreenDesc: 'OCR engine processes screen content to read coding problems and visual context. Supports both cloud and local OCR processing.',

  techAiTitle: 'AI Backend',
  techAiDesc: 'AI analyzes text to detect questions and generate contextual hints in real-time. Supports cloud and local LLM providers.',

  faqTitle: 'Frequently Asked Questions',
  
  faq1Question: 'What makes this different from recording and reviewing later?',
  faq1Answer: 'Intervio provides real-time hints during your session, helping you improve your answers as you speak. This immediate feedback helps you perform better in the moment.',

  faq2Question: 'Do I need an internet connection?',
  faq2Answer: 'It depends on your setup. In cloud mode, an internet connection is required for speech-to-text, OCR, and AI hints. In local mode, speech-to-text and OCR work fully offline, but an internet connection is still needed for AI hint generation unless you use a local LLM.',

  faq3Question: 'What types of interviews does it support?',
  faq3Answer: 'The system works with technical coding interviews, system design discussions, behavioral interviews (STAR format), and general conversation. The AI adapts its hints based on the question type detected.',
  
  faq4Question: 'Can I use it with video call platforms?',
  faq4Answer: 'Yes! Intervio captures your system audio and screen content, so it works alongside Zoom, Google Meet, or any video platform you use for your interviews.',

  faq5Question: 'Is my data stored anywhere?',
  faq5Answer: 'In local mode, audio and screen data never leave your device. In cloud mode, data is processed through cloud services in real-time. In both cases, we do not retain your interview content long-term after sessions end.',

  // Privacy Page
  privacyPageTitle: 'Privacy Policy',
  privacyLastUpdated: 'Last updated: March 2026',

  privacyIntro: "Intervio supports both cloud and local processing modes for real-time transcription and AI-powered hints. This policy explains how we handle your data in each mode.",

  privacyArchitectureTitle: 'Our Architecture',
  privacyArchitectureIntro: 'Intervio offers two processing modes to balance accuracy and privacy:',
  privacyArchAudio: 'Cloud mode: Audio is sent to cloud speech-to-text services for transcription. Local mode: Audio is transcribed on your device using local Whisper models — it never leaves your computer.',
  privacyArchScreen: 'Cloud mode: Screen content is sent to cloud OCR services. Local mode: Screen content is processed by a local OCR engine on your device.',
  privacyArchText: 'In both modes, text transcriptions and OCR results are sent to AI services for question detection and hint generation.',

  privacyDataTitle: 'What Data We Process',
  privacyDataLocalTitle: 'Data Captured by the Application',
  privacyDataLocal1: 'Audio recordings from your microphone',
  privacyDataLocal2: 'Screen captures and screenshots',
  privacyDataLocal3: 'Raw video feeds from cameras',
  privacyDataLocal4: 'Application window contents',
  
  privacyDataSentTitle: 'Data Sent to Cloud Services (Cloud Mode)',
  privacyDataSent1: 'Audio data for cloud speech-to-text processing',
  privacyDataSent2: 'Screen content for cloud OCR processing',
  privacyDataSent3: 'Text transcripts for AI question detection and hints',

  privacyDataLocalOnlyTitle: 'Data Processed Locally (Local Mode)',
  privacyDataLocalOnly1: 'Audio is transcribed on-device using local Whisper models',
  privacyDataLocalOnly2: 'Screen content is processed by local OCR engine',
  privacyDataLocalOnly3: 'Only text transcripts are sent to AI services for hints',

  privacyDataNotTitle: 'Data We Do NOT Store Long-Term',
  privacyDataNot1: 'Audio recordings after processing',
  privacyDataNot2: 'Screenshots after processing',
  privacyDataNot3: 'Video of your face or environment',
  privacyDataNot4: 'Personal identification information',
  privacyDataNot5: 'Interview content after sessions end',
  
  privacyUsageTitle: 'How We Use Your Data',
  privacyUsageIntro: 'Text data sent to our servers is used solely for:',
  privacyUsage1: 'Detecting questions in the conversation',
  privacyUsage2: 'Generating relevant hints and suggestions',
  privacyUsage3: 'Improving our AI models (anonymized and aggregated only)',
  privacyUsageNote: 'We do not sell, rent, or share your personal data with third parties for marketing purposes.',
  
  privacyRetentionTitle: 'Data Retention',
  privacyRetentionSession: 'Data processed during your interview session is not stored long-term after the session ends. We process them in real-time and discard them after processing.',
  privacyRetentionAccount: 'If you create an account, we retain your email address and account preferences until you request deletion.',
  privacyRetentionAnalytics: 'We may retain anonymized, aggregated usage statistics to improve our service.',
  
  privacyThirdPartyTitle: 'Third-Party Services',
  privacyThirdPartyIntro: 'Depending on your processing mode, we may use third-party cloud services for speech-to-text, OCR, and AI hints. These providers:',
  privacyThirdParty1: 'Process audio, screen content, and text data according to their service agreements',
  privacyThirdParty2: 'Are bound by their own privacy policies and data handling agreements',
  privacyThirdParty3: 'Do not receive personally identifiable information from us',
  
  privacyRightsTitle: 'Your Rights',
  privacyRightsIntro: 'You have the right to:',
  privacyRights1: 'Access any personal data we hold about you',
  privacyRights2: 'Request deletion of your account and associated data',
  privacyRights3: 'Opt out of any analytics or model improvement programs',
  privacyRights4: 'Export your data in a portable format',
  privacyRightsContact: 'To exercise these rights, contact us at',
  
  privacySecurityTitle: 'Security',
  privacySecurityIntro: 'We implement appropriate technical and organizational measures to protect your data, including:',
  privacySecurity1: 'Encryption in transit (TLS/HTTPS)',
  privacySecurity2: 'Secure WebSocket connections for real-time communication',
  privacySecurity3: 'Regular security audits and updates',
  privacySecurity4: 'Access controls and authentication',
  
  privacyChildrenTitle: "Children's Privacy",
  privacyChildrenText: 'Intervio is not intended for use by children under 13. We do not knowingly collect personal information from children.',

  privacyChangesTitle: 'Changes to This Policy',
  privacyChangesText: 'We may update this privacy policy from time to time. We will notify you of any changes by posting the new policy on this page and updating the "Last updated" date.',
  
  privacyContactTitle: 'Contact Us',
  privacyContactText: 'If you have questions about this privacy policy or our data practices, please contact us at:',
  
  privacySummaryTitle: 'Summary: How We Handle Data',
  privacySummary1: 'Cloud or local processing — your choice',
  privacySummary2: 'Screen content analyzed via OCR',
  privacySummary3: 'No long-term session data stored',
  privacySummary4: 'Local mode keeps audio on device',
  privacySummary5: 'No selling of data',
  privacySummary6: 'No voice recordings stored long-term',

  // Pricing Page
  navPricing: 'Pricing',
  pricingPageTitle: 'Simple, Transparent Pricing',
  pricingPageSubtitle: 'Try Intervio free for 3 hours. All features included.',

  pricingMonthly: 'Monthly',
  pricingAnnual: 'Annual',
  pricingSavePercent: 'Save 76%',

  pricingPerMonth: '/month',
  pricingPerYear: '/year',
  pricingBilledAnnually: 'Billed annually',
  pricingBilledMonthly: 'Billed monthly',

  // Free Trial
  pricingFreePlan: 'Free Trial',
  pricingFreePrice: '$0',
  pricingFreeDesc: 'Try all features free for 3 hours',
  pricingFreeFeature1: '3 hours of full access',
  pricingFreeFeature2: 'All features included',
  pricingFreeFeature3: 'Real-time transcription',
  pricingFreeFeature4: 'AI hints (depth & lateral)',
  pricingFreeFeature5: 'Screen content recognition',
  pricingFreeFeature6: 'No credit card required',
  pricingFreeCta: 'Start Free Trial',

  // Basic Plan
  pricingBasicPlan: 'Basic',
  pricingBasicMonthly: '$29',
  pricingBasicAnnual: '$79',
  pricingBasicMonthlyEquiv: '$6.58/mo',
  pricingBasicDesc: '10 hours/month for casual interview prep',
  pricingBasicFeature1: '10 hours of session time per month',
  pricingBasicFeature2: 'All features included',
  pricingBasicFeature3: 'Real-time transcription',
  pricingBasicFeature4: 'AI hints (depth & lateral)',
  pricingBasicFeature5: 'Screen content recognition',
  pricingBasicFeature6: 'Email support',
  pricingBasicCta: 'Get Basic',

  // Pro Plan
  pricingProPlan: 'Pro',
  pricingProMonthly: '$69',
  pricingProAnnual: '$199',
  pricingProMonthlyEquiv: '$16.58/mo',
  pricingProDesc: '30 hours/month for serious interview prep',
  pricingProFeature1: '30 hours of session time per month',
  pricingProFeature2: 'All features included',
  pricingProFeature3: 'Real-time transcription',
  pricingProFeature4: 'AI hints (depth & lateral)',
  pricingProFeature5: 'Screen content recognition',
  pricingProFeature6: 'Priority support',
  pricingProCta: 'Get Pro',
  pricingProBadge: 'Best Value',
  pricingCurrentPlan: 'Current Plan',



  // Checkout
  checkoutTitle: 'Complete Your Order',
  checkoutPlan: 'Plan',
  checkoutBilling: 'Billing Cycle',
  checkoutTotal: 'Total',
  checkoutEmail: 'Email Address',
  checkoutEmailPlaceholder: 'you@example.com',
  checkoutButton: 'Continue to Payment',
  checkoutProcessing: 'Processing...',
  checkoutSecure: 'Secure checkout powered by 2Checkout',
  checkoutTerms: 'By continuing, you agree to our Terms of Service and Privacy Policy.',
  checkoutSuccess: 'Order received! Check your email for next steps.',
  checkoutError: 'Something went wrong. Please try again.',

  // Auth Pages
  loginTitle: 'Welcome Back',
  loginSubtitle: 'Log in to your account to continue.',
  loginButton: 'Log In',
  loginError: 'Invalid email or password.',
  loginNoAccount: "Don't have an account?",
  loginSignUpLink: 'Sign up',
  loginForgotPassword: 'Forgot your password?',

  // Forgot Password
  forgotPasswordTitle: 'Forgot Password',
  forgotPasswordSubtitle: 'Enter your email and we\'ll send you a link to reset your password.',
  forgotPasswordButton: 'Send Reset Link',
  forgotPasswordSending: 'Sending...',
  forgotPasswordSuccess: 'If an account with that email exists, a password reset link has been sent. Check your inbox.',
  forgotPasswordBackToLogin: 'Back to Log In',

  // Reset Password
  resetPasswordTitle: 'Reset Password',
  resetPasswordSubtitle: 'Enter your new password below.',
  resetPasswordNewPassword: 'New Password',
  resetPasswordConfirm: 'Confirm New Password',
  resetPasswordButton: 'Reset Password',
  resetPasswordResetting: 'Resetting...',
  resetPasswordSuccess: 'Your password has been reset successfully!',
  resetPasswordGoToLogin: 'Go to Log In',
  resetPasswordMismatch: 'Passwords do not match.',
  resetPasswordInvalidToken: 'Invalid or expired reset link. Please request a new one.',

  registerTitle: 'Create Your Account',
  registerSubtitle: 'Start your free trial — all features included.',
  registerButton: 'Create Account',
  registerError: 'Could not create account. Please try again.',
  registerPasswordMismatch: 'Passwords do not match.',
  registerTerms: 'By creating an account, you agree to our Terms of Service and Privacy Policy.',
  registerHaveAccount: 'Already have an account?',
  registerSignInLink: 'Log in',

  authEmail: 'Email Address',
  authEmailPlaceholder: 'you@example.com',
  authPassword: 'Password',
  authPasswordPlaceholder: 'Min. 8 characters',
  authConfirmPassword: 'Confirm Password',
  authConfirmPlaceholder: 'Re-enter your password',
  authLoggingIn: 'Logging in...',
  authCreatingAccount: 'Creating account...',

  // Dashboard
  dashboardTitle: 'Dashboard',
  dashboardLoading: 'Loading...',
  dashboardLogout: 'Log Out',
  dashboardSubscription: 'Subscription',
  dashboardBadgeTrial: 'Free Trial',
  dashboardBadgePro: 'PRO',
  dashboardBadgeBasic: 'BASIC',
  dashboardBadgeExpired: 'Expired',
  dashboardUsed: 'Used',
  dashboardRemaining: 'Remaining',
  dashboardBasicActive: 'You have 10 hours/month of access to all features.',
  dashboardProActive: 'You have 30 hours/month of access to all features.',
  dashboardTrialExpired: 'Your free trial has ended. Subscribe to continue using Intervio.',
  dashboardUpgrade: 'Upgrade to Pro',
  dashboardDownloadTitle: 'Desktop Application',
  dashboardDownloadDesc: 'Download the desktop app to use Intervio during your interviews. Log in with the same account.',
  dashboardDownloadNote: 'Available for macOS, Windows, Linux, and Android.',

  // Interview Profile
  profileTitle: 'Interview Profile',
  profileDesc: 'Add your details for personalized AI hints tailored to your background and target role.',
  profilePosition: 'Target Position',
  profilePositionPlaceholder: 'e.g. Senior Backend Engineer',
  profileCompany: 'Company',
  profileCompanyPlaceholder: 'e.g. Google',
  profileJobDescription: 'Job Description',
  profileJobDescriptionPlaceholder: 'Paste the job description here or upload a file...',
  profileCvText: 'CV / Resume',
  profileCvTextPlaceholder: 'Paste your CV text here or upload a file...',
  profileSave: 'Save Profile',
  profileCreate: 'Create Profile',
  profileSaving: 'Saving...',
  profileSaved: 'Profile saved!',
  profileSaveError: 'Failed to save profile. Please try again.',
  profileClear: 'Clear All',
  profileSummaryReady: 'AI summary generated',
  profileSummaryPending: 'AI summary will be generated after save',
  profileNewProfile: 'New Profile',
  profileActive: 'ACTIVE',
  profileEdit: 'Edit Profile',
  profileDelete: 'Delete',
  profileDeleteConfirm: 'Are you sure you want to delete this profile?',
  profileCancel: 'Cancel',
  profileName: 'Profile Name',
  profileNamePlaceholder: 'e.g. Google Backend, Meta Frontend',
  profileTargetRole: 'Target Role',
  profileNotSet: 'Not specified yet',
  profileCvSummaryTitle: 'CV Summary',
  profileJdSummaryTitle: 'Job Description Summary',
  profileCvNotUploaded: 'No CV uploaded yet',
  profileJdNotUploaded: 'No job description added yet',
  profileEmptyState: 'Create your first interview profile to get personalized AI hints.',
  profileCreateFirst: 'Create Your First Profile',
  profileUploadFile: 'Upload File',
  profileUploading: 'Uploading...',
  profileFileHint: 'Supports PDF, DOCX, TXT (max 5 MB)',
  profileFileUploaded: 'File text extracted! Save the profile to generate AI summary.',

  // Checkout Success
  checkoutSuccessLoading: 'Loading...',
  checkoutSuccessProcessing: 'Processing Your Payment',
  checkoutSuccessWait: 'Please wait while we confirm your payment. This usually takes a few seconds.',
  checkoutSuccessTitle: 'Payment Successful!',
  checkoutSuccessDesc: 'Your subscription is now active. Enjoy full access to Intervio.',
  checkoutSuccessGoToDashboard: 'Go to Dashboard',
  checkoutSuccessPending: 'Payment Received',
  checkoutSuccessPendingDesc: 'Your payment is being processed. It may take a few minutes for your subscription to activate.',

  // Platforms Section
  platformsTitle: 'Works With All Major Platforms',

  // Social Proof Section
  socialProofTitle: 'What Our Beta Testers Say',
  socialProofWaitlist: 'users and counting',
  socialProofTestimonial1Name: 'Arjun M.',
  socialProofTestimonial1Role: 'Backend Developer',
  socialProofTestimonial1Text: 'I had three rounds back to back with Epam and honestly I was blanking on system design. The depth hints reminded me to mention caching layers and I could tell the interviewer liked that. Got the offer two days later.',
  socialProofTestimonial2Name: 'Wei L.',
  socialProofTestimonial2Role: 'Full-Stack Engineer',
  socialProofTestimonial2Text: 'My biggest worry was the screen share thing. Tested it with a friend on Zoom first — literally nothing shows up on their end. Used it in two real interviews after that, no problems at all.',
  socialProofTestimonial3Name: 'Priya S.',
  socialProofTestimonial3Role: 'Software Engineer',
  socialProofTestimonial3Text: 'Had a CodeSignal assessment and I was stuck on a graph problem. Intervio picked it up from my screen and nudged me toward BFS. Didn\'t give me the answer outright but the hint was enough to get me unstuck.',
  socialProofCompaniesTitle: 'Beta testers passing interviews at',

  // Why Intervio Section
  whyIntervioTitle: 'Why Intervio?',
  whyIntervioSubtitle: 'See how Intervio compares to other interview assistants.',
  whyIntervioFeature: 'Feature',
  whyIntervioIntervio: 'Intervio',
  whyIntervioOthers: 'Others',
  whyIntervioRow1: 'Depth + Lateral hints',
  whyIntervioRow2: 'Local processing option',
  whyIntervioRow3: 'No API keys needed',
  whyIntervioRow4: 'Annual price',
  whyIntervioRow4Intervio: '$199/yr',
  whyIntervioRow4Others: '$300–600/yr',
  whyIntervioRow5: 'Cross-platform',
  whyIntervioRow5Intervio: 'macOS, Windows, Linux, Android',
  whyIntervioRow5Others: 'macOS only',
  whyIntervioRow6: 'Screen-share invisible',

  // Homepage FAQ Section
  homeFaqTitle: 'Frequently Asked Questions',
  homeFaq1Question: 'Is Intervio visible during screen sharing?',
  homeFaq1Answer: 'No. On macOS and Windows, Intervio is completely invisible to Zoom, Google Meet, Microsoft Teams, and all other screen-sharing tools. It uses OS-level content protection to exclude itself from screen captures and recordings.',
  homeFaq2Question: 'Does it work with Zoom, Teams, and Google Meet?',
  homeFaq2Answer: 'Yes! Intervio works alongside any video conferencing platform. It captures your system audio to hear the interviewer and provides real-time hints without being detected by the screen-sharing stream.',
  homeFaq3Question: 'What about coding interviews?',
  homeFaq3Answer: 'Intervio reads coding problems directly from your screen using OCR — works with HackerRank, CodeSignal, LeetCode, CoderPad, and any browser-based coding platform. It detects the problem and gives you algorithm hints, approach suggestions, and solution outlines.',
  homeFaq4Question: 'Is my data safe?',
  homeFaq4Answer: 'Yes. Choose local processing mode and your audio and screen data never leave your device. Even in cloud mode, we do not store your interview sessions. Data is processed in real-time and discarded immediately.',
  homeFaq5Question: 'How is Intervio different from other interview assistants?',
  homeFaq5Answer: 'Intervio offers unique Depth and Lateral hints (not just answers), a local processing mode for maximum privacy, no API keys required, and full cross-platform support on macOS, Windows, Linux, and Android.',
}
